// Journal entries: the shared source of truth between iOS and the browser.
//
// The push payload here is field-for-field identical to ios-app/.../Core/Network/SyncService.swift,
// and the pull shape matches what the Worker returns from GET /api/entries. Title, content and the
// dream detail blob travel as base64 of an AES-GCM box sealed with the user's DEK; everything else
// is cleartext metadata the server indexes and reasons over.
//
// Anything that would let the server read the writing itself belongs in the ciphertext. Anything
// the server needs to group, filter or analyse — kind, dates, symbols — is deliberately cleartext.

import { getToken, apiDelete, apiGet, apiPost } from './client';
import { openWithKey, sealWithKey } from '../crypto/crypto-vault';
import { hasStructuredDreamData, parseDreamDetails } from './dream-details';

export type { DreamDetails, DreamPerspective, DreamTone } from './dream-details';
export {
  derivedDreamType,
  deriveSymbols,
  emptyDreamDetails,
  hasStructuredDreamData,
  parseDreamDetails,
} from './dream-details';

import type { DreamDetails } from './dream-details';

export type EntryKind = 'waking' | 'dream';

// The three iOS knows about. `DreamType(rawValue:)` returns nil for anything else, so sending a fourth
// kind would land on the phone as a dream with no type at all rather than as the type intended.
export type DreamType = 'normal' | 'lucid' | 'nightmare';

export type ExperiencePrecision = 'exact' | 'day' | 'month' | 'year' | 'period';

/** A decrypted entry, as the UI works with it. */
export interface Entry {
  id: string;
  kind: EntryKind;
  title: string;
  body: string;
  dreamType: DreamType | null;
  isOblivion: boolean;
  dreamDetails: DreamDetails | null;
  symbols: string[];
  createdAt: Date;
  updatedAt: Date;
  /** When it actually happened, which is not always when it was written down. */
  experienceAt: Date;
  experiencePrecision: ExperiencePrecision | null;
  lifePeriod: string | null;
  isHistorical: boolean;
  /**
   * Started but not finished. A draft is a real, synced entry — it just hasn't been let into the
   * journal yet, so it lives in the Drafts section and is kept out of everything Oreia reads.
   */
  isDraft: boolean;
}

export interface EntryDraft {
  id?: string;
  kind: EntryKind;
  title: string;
  body: string;
  dreamType?: DreamType | null;
  isOblivion?: boolean;
  dreamDetails?: DreamDetails | null;
  symbols?: string[];
  createdAt?: Date;
  experienceAt?: Date;
  experiencePrecision?: ExperiencePrecision | null;
  lifePeriod?: string | null;
  isHistorical?: boolean;
  /** Omitted means published: only the composer's autosave deliberately writes a draft. */
  isDraft?: boolean;
}

interface ServerEntry {
  id: string;
  entry_type?: string | null;
  title?: string | null;
  content?: string | null;
  dream_type?: string | null;
  is_oblivion?: number | null;
  symbols_json?: string | null;
  oblivion_cipher?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  deleted?: number | null;
  content_encrypted?: number | null;
  experience_at?: string | null;
  experience_precision?: string | null;
  life_period?: string | null;
  is_historical?: number | null;
  is_draft?: number | null;
}

interface EntriesResponse {
  next_cursor?: string | null;
  entries: ServerEntry[];
}

/**
 * The Worker accepts both ISO 8601 and "yyyy-MM-dd HH:mm:ss" UTC, and returns either depending on
 * whether a row was written by a client or by SQLite's datetime('now'). Normalise both, the same
 * way SyncService.parseDate does on iOS.
 */
function parseDate(raw: string | null | undefined, fallback: Date): Date {
  if (!raw) return fallback;
  const iso = new Date(raw);
  if (!Number.isNaN(iso.getTime())) return iso;
  const sqlite = new Date(`${raw.replace(' ', 'T')}Z`);
  return Number.isNaN(sqlite.getTime()) ? fallback : sqlite;
}

function parseOptionalDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const parsed = parseDate(raw, new Date(NaN));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseSymbols(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Decrypt one server row. Rows written before envelope encryption have content_encrypted = 0 and
 * hold plaintext; the Worker's own read path tolerates those, so we do too rather than showing a
 * user an empty entry they know they wrote.
 */
async function decryptRow(row: ServerEntry, dek: CryptoKey): Promise<Entry> {
  const encrypted = (row.content_encrypted ?? 1) === 1;

  const open = async (value: string | null | undefined): Promise<string> => {
    if (!value) return '';
    if (!encrypted) return value;
    try {
      return await openWithKey(dek, value);
    } catch {
      // A box we cannot open means the wrong key or a corrupt row. Returning a marker rather than
      // throwing keeps one bad entry from blanking the whole journal.
      return '';
    }
  };

  let dreamDetails: DreamDetails | null = null;
  if (row.oblivion_cipher) {
    const raw = await open(row.oblivion_cipher);
    if (raw) {
      try {
        // Normalised rather than cast. The blob is whatever some version of some client wrote, and a
        // cast would leave the UI binding to fields that are the wrong type or absent.
        dreamDetails = parseDreamDetails(JSON.parse(raw));
      } catch {
        dreamDetails = null;
      }
    }
  }

  const createdAt = parseDate(row.created_at, new Date());

  return {
    id: row.id,
    kind: row.entry_type === 'dream' ? 'dream' : 'waking',
    title: await open(row.title),
    body: await open(row.content),
    dreamType: (row.dream_type as DreamType | null) ?? null,
    isOblivion: (row.is_oblivion ?? 0) === 1,
    dreamDetails,
    symbols: parseSymbols(row.symbols_json),
    createdAt,
    updatedAt: parseDate(row.updated_at, createdAt),
    experienceAt: parseOptionalDate(row.experience_at) ?? createdAt,
    experiencePrecision: (row.experience_precision as ExperiencePrecision | null) ?? null,
    lifePeriod: row.life_period ?? null,
    isHistorical: (row.is_historical ?? 0) === 1,
    // Absent means published. A server that predates the column, or an entry written by iOS, must
    // not have its entries quietly filed as unfinished and disappear from the journal.
    isDraft: (row.is_draft ?? 0) === 1,
  };
}

export interface FetchEntriesOptions {
  limit?: number;
  /** ISO timestamp; only rows changed since then come back. */
  since?: string;
  signal?: AbortSignal;
}

/** Fetch and decrypt entries. Tombstones (deleted = 1) are filtered out. */
export async function fetchEntries(
  dek: CryptoKey,
  options: FetchEntriesOptions = {},
): Promise<Entry[]> {
  const params = new URLSearchParams({ limit: String(options.limit ?? 100) });
  if (options.since) params.set('since', options.since);

  const rows = new Map<string, ServerEntry>();
  const seen = new Set<string>();
  let path = `/api/entries?${params}`;
  while (true) {
    options.signal?.throwIfAborted();
    const res = await apiGet<EntriesResponse>(path, options.signal);
    options.signal?.throwIfAborted();
    for (const row of res.entries ?? []) rows.set(row.id, row);
    if (!res.next_cursor) break;
    if (seen.has(res.next_cursor)) throw new Error('Journal download did not advance. Please retry.');
    seen.add(res.next_cursor);
    path = `/api/entries?limit=${options.limit ?? 100}&cursor=${encodeURIComponent(res.next_cursor)}`;
  }
  const live = [...rows.values()].filter((row) => (row.deleted ?? 0) !== 1);
  return Promise.all(live.map((row) => decryptRow(row, dek)));
}

/**
 * Encrypt and push a draft. Returns the entry as it now exists, so the caller can render it
 * without a round trip.
 *
 * The server treats this as an upsert keyed on id and scoped to the authenticated user, so
 * re-pushing the same id edits in place — which is how an autosaving composer works.
 */
export async function saveEntry(dek: CryptoKey, draft: EntryDraft): Promise<Entry> {
  const sessionToken=getToken();
  const now = new Date();
  const id = draft.id ?? crypto.randomUUID();
  const createdAt = draft.createdAt ?? now;
  const experienceAt = draft.experienceAt ?? createdAt;
  const isDream = draft.kind === 'dream';

  // Sealed only when the user actually put something in the form, which mirrors how iOS decides:
  // clearing every field removes the blob on both clients. Counting keys was the wrong test now that
  // the payload always carries all of them — every dream would have written a payload of pure
  // defaults, and an entry with no details would have looked, to iOS, like one deliberately set to
  // neutral/first/2/3.
  const details = draft.dreamDetails ?? null;
  const detailsJson = details && hasStructuredDreamData(details) ? JSON.stringify(details) : null;

  const payload = {
    id,
    entry_type: isDream ? 'dream' : 'journal',
    kind: draft.kind,
    dream_type: isDream ? (draft.dreamType ?? 'normal') : null,
    is_oblivion: draft.isOblivion ?? false,
    symbols: draft.symbols ?? [],
    title: await sealWithKey(dek, draft.title),
    content: await sealWithKey(dek, draft.body),
    oblivion_cipher: detailsJson ? await sealWithKey(dek, detailsJson) : null,
    created_at: createdAt.toISOString(),
    updated_at: now.toISOString(),
    content_encrypted: true,
    experience_at: experienceAt.toISOString(),
    experience_precision: draft.experiencePrecision ?? null,
    life_period: draft.lifePeriod ?? null,
    is_historical: draft.isHistorical ?? false,
    is_draft: draft.isDraft ?? false,
  };

  if(getToken()!==sessionToken)throw new Error('Your session changed. Reopen your journal to continue.');
  await apiPost('/api/entries/sync', { entries: [payload] });

  return {
    id,
    kind: draft.kind,
    title: draft.title,
    body: draft.body,
    dreamType: isDream ? (draft.dreamType ?? 'normal') : null,
    isOblivion: draft.isOblivion ?? false,
    dreamDetails: draft.dreamDetails ?? null,
    symbols: draft.symbols ?? [],
    createdAt,
    updatedAt: now,
    experienceAt,
    experiencePrecision: draft.experiencePrecision ?? null,
    lifePeriod: draft.lifePeriod ?? null,
    isHistorical: draft.isHistorical ?? false,
    isDraft: draft.isDraft ?? false,
  };
}

/**
 * Hard-delete an entry. The server marks the row deleted before responding, so a later pull can
 * only ever confirm the deletion — it can't resurrect the entry, which is the bug the iOS app hit
 * back when deletion was a local soft-delete tombstone.
 */
export async function deleteEntry(id: string): Promise<void> {
  await apiDelete(`/api/entries/${encodeURIComponent(id)}`);
}

/** Ask the server to reflect now instead of waiting for the nightly pass. */
export async function requestReflection(): Promise<void> {
  await apiPost('/api/reflect/run', {});
}

/**
 * Delete every entry and everything derived from it, and return how many entries went.
 *
 * Wider than it sounds, deliberately: the Worker also clears the insights, entities, links, facet
 * scores and archetype memories built out of those entries. Leaving those behind would give a
 * portrait with nothing under it — Oreia would go on describing someone from evidence the person had
 * just asked to destroy. The account, vault keys and settings survive.
 */
export async function eraseAllEntries(): Promise<number> {
  const res = await apiPost<{ erased?: number }>('/api/entries/erase-all', {});
  return res.erased ?? 0;
}

/**
 * Suggest a title for a draft. Fails soft: the server route is explicitly best-effort, and a
 * missing suggestion should never block someone from saving what they wrote.
 */
export async function suggestTitle(body: string, kind: EntryKind): Promise<string | null> {
  try {
    const res = await apiPost<{ title?: string }>('/api/title', { body, kind });
    return res.title ?? null;
  } catch {
    return null;
  }
}
